import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCaeExceptionsSummary } from '../../../hooks/analytics/useCaeReports.ts';
import { formatPercent, WidgetCard } from '../controller/shared.tsx';
import { BAR_RADIUS, CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

/** Report #14 — Exceptions Summary. Bar chart of top controls by exception count, with expandable detail rows. */
export const ExceptionsSummaryChart: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeExceptionsSummary();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (b.exceptions ?? 0) - (a.exceptions ?? 0))
        .slice(0, 10)
        .map((r) => ({ control_id: r.control_id, exceptions: r.exceptions ?? 0 })),
    [rows]
  );

  return (
    <WidgetCard
      title="Exceptions Summary"
      description="Top controls by total exceptions across current test runs"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load exceptions summary.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No exceptions recorded."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="control_id" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
            <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
            <Bar dataKey="exceptions" fill={SEMANTIC.critical.chart} radius={BAR_RADIUS} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="analytics-scroll mt-3 border-t border-slate-100 pt-3 max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="pb-2 w-6" />
              <th className="pb-2">Control</th>
              <th className="pb-2 text-right">Exceptions</th>
              <th className="pb-2 text-right">Avg Deviation</th>
              <th className="pb-2 text-right">Failed Runs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const isOpen = expanded === row.control_id;
              return (
                <React.Fragment key={row.control_id}>
                  <tr
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : row.control_id)}
                  >
                    <td className="py-1.5">
                      {isOpen ? (
                        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-900">{row.control_id}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-rose-600">{row.exceptions ?? 0}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-500">
                      {row.avg_deviation != null ? formatPercent(row.avg_deviation * 100, 1) : '—'}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{row.failed_runs}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="pb-3 pt-1 pl-6 text-xs text-slate-500 bg-slate-50">
                        {row.title}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
};

export default ExceptionsSummaryChart;
